import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { trpc } from '@/lib/trpc';

export default function DebugLogs() {
  const [pattern, setPattern] = useState('ensureClassificationDraft');
  const [functionName, setFunctionName] = useState('ensureClassificationDraft');
  const [operationName, setOperationName] = useState('ensureClassificationDraft');
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Queries
  const allLogsQuery = trpc.debug.logs.getAll.useQuery(undefined, { refetchInterval: autoRefresh ? 2000 : false });
  const patternLogsQuery = trpc.debug.logs.getByPattern.useQuery(
    { pattern, limit: 100 },
    { refetchInterval: autoRefresh ? 2000 : false }
  );
  const functionLogsQuery = trpc.debug.logs.getByFunction.useQuery(
    { functionName },
    { refetchInterval: autoRefresh ? 2000 : false }
  );
  const timingQuery = trpc.debug.logs.getOperationTiming.useQuery(
    { operationName },
    { refetchInterval: autoRefresh ? 2000 : false }
  );
  const statsQuery = trpc.debug.logs.getStats.useQuery(undefined, { refetchInterval: autoRefresh ? 2000 : false });

  const handleClearLogs = async () => {
    // Note: clearDebugLogs é uma mutation, mas não está exposta no router
    // Você pode adicionar depois se necessário
    alert('Limpar logs não está implementado ainda');
  };

  const handleRefresh = () => {
    allLogsQuery.refetch();
    patternLogsQuery.refetch();
    functionLogsQuery.refetch();
    timingQuery.refetch();
    statsQuery.refetch();
  };

  return (
    <div className="container mx-auto py-8 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Debug Logs</h1>
          <p className="text-gray-500">Visualize logs do servidor em tempo real</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => setAutoRefresh(!autoRefresh)}
            variant={autoRefresh ? "default" : "outline"}
          >
            {autoRefresh ? '🔄 Auto-refresh ON' : '⏸ Auto-refresh OFF'}
          </Button>
          <Button onClick={handleRefresh} variant="outline">
            🔄 Refresh Manual
          </Button>
        </div>
      </div>

      {/* Stats Card */}
      {statsQuery.data && (
        <Card>
          <CardHeader>
            <CardTitle>Estatísticas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-500">Total de Logs</p>
                <p className="text-2xl font-bold">{statsQuery.data.totalLogs}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Logs (log)</p>
                <p className="text-2xl font-bold text-blue-600">{statsQuery.data.byLevel.log}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Logs (error)</p>
                <p className="text-2xl font-bold text-red-600">{statsQuery.data.byLevel.error}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Logs (warn)</p>
                <p className="text-2xl font-bold text-yellow-600">{statsQuery.data.byLevel.warn}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="timing" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="timing">Timing</TabsTrigger>
          <TabsTrigger value="function">Função</TabsTrigger>
          <TabsTrigger value="pattern">Padrão</TabsTrigger>
          <TabsTrigger value="all">Todos</TabsTrigger>
        </TabsList>

        {/* Timing Tab */}
        <TabsContent value="timing" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Timing da Operação</CardTitle>
              <CardDescription>Mostra timing detalhado de cada etapa</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  placeholder="Nome da operação (ex: ensureClassificationDraft)"
                  value={operationName}
                  onChange={(e) => setOperationName(e.target.value)}
                />
                <Button onClick={() => timingQuery.refetch()}>Buscar</Button>
              </div>

              {timingQuery.isLoading && <p>Carregando...</p>}
              {timingQuery.error && <p className="text-red-600">Erro: {timingQuery.error.message}</p>}
              {timingQuery.data && (
                <div className="space-y-4">
                  {(timingQuery.data as any).error ? (
                    <p className="text-red-600">{(timingQuery.data as any).error}</p>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-500">Tempo Total</p>
                          <p className="text-2xl font-bold">
                            {(timingQuery.data as any).totalTime ? `${(timingQuery.data as any).totalTime}ms` : 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Status</p>
                          <p className="text-lg font-bold">
                            {(timingQuery.data as any).hasError ? '❌ ERRO' : '✅ SUCESSO'}
                          </p>
                        </div>
                      </div>

                      {(timingQuery.data as any).stageTimings && Object.keys((timingQuery.data as any).stageTimings).length > 0 && (
                        <div className="space-y-2">
                          <p className="font-bold">Timing por Etapa:</p>
                          {Object.entries((timingQuery.data as any).stageTimings).map(([stage, time]) => (
                            <div key={stage} className="flex justify-between items-center bg-gray-100 p-2 rounded">
                              <span>{stage}</span>
                              <span className={`font-bold ${(time as number) > 5000 ? 'text-red-600' : 'text-green-600'}`}>
                                {time}ms
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      {(timingQuery.data as any).errorMessage && (
                        <div className="bg-red-100 border border-red-400 text-red-700 p-3 rounded">
                          <p className="font-bold">Erro:</p>
                          <p className="text-sm">{(timingQuery.data as any).errorMessage}</p>
                        </div>
                      )}

                      <details className="bg-gray-100 p-3 rounded">
                        <summary className="cursor-pointer font-bold">Ver Logs Brutos ({(timingQuery.data as any).logCount})</summary>
                        <pre className="mt-2 text-xs overflow-auto max-h-96 bg-white p-2 rounded border">
                          {JSON.stringify((timingQuery.data as any).logs, null, 2)}
                        </pre>
                      </details>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Function Tab */}
        <TabsContent value="function" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Logs por Função</CardTitle>
              <CardDescription>Filtra logs de uma função específica</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  placeholder="Nome da função (ex: ensureClassificationDraft)"
                  value={functionName}
                  onChange={(e) => setFunctionName(e.target.value)}
                />
                <Button onClick={() => functionLogsQuery.refetch()}>Buscar</Button>
              </div>

              {functionLogsQuery.isLoading && <p>Carregando...</p>}
              {functionLogsQuery.error && <p className="text-red-600">Erro: {functionLogsQuery.error.message}</p>}
              {functionLogsQuery.data && (
                <div className="space-y-2 max-h-96 overflow-auto">
                  {(functionLogsQuery.data as any).length === 0 ? (
                    <p className="text-gray-500">Nenhum log encontrado</p>
                  ) : (
                    (functionLogsQuery.data as any).map((log: any, idx: number) => (
                      <div key={idx} className={`p-2 rounded text-sm font-mono ${
                        log.level === 'error' ? 'bg-red-100' :
                        log.level === 'warn' ? 'bg-yellow-100' :
                        'bg-blue-100'
                      }`}>
                        <div className="flex justify-between">
                          <span className="font-bold">[{log.level.toUpperCase()}]</span>
                          <span className="text-gray-500">{new Date(log.timestamp).toLocaleTimeString()}</span>
                        </div>
                        <div className="mt-1">{log.message}</div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pattern Tab */}
        <TabsContent value="pattern" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Logs por Padrão</CardTitle>
              <CardDescription>Filtra logs usando regex</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  placeholder="Padrão regex (ex: ETAPA|ERRO|FIM)"
                  value={pattern}
                  onChange={(e) => setPattern(e.target.value)}
                />
                <Button onClick={() => patternLogsQuery.refetch()}>Buscar</Button>
              </div>

              {patternLogsQuery.isLoading && <p>Carregando...</p>}
              {patternLogsQuery.error && <p className="text-red-600">Erro: {patternLogsQuery.error.message}</p>}
              {patternLogsQuery.data && (
                <div className="space-y-2 max-h-96 overflow-auto">
                  {(patternLogsQuery.data as any).length === 0 ? (
                    <p className="text-gray-500">Nenhum log encontrado</p>
                  ) : (
                    (patternLogsQuery.data as any).map((log: any, idx: number) => (
                      <div key={idx} className={`p-2 rounded text-sm font-mono ${
                        log.level === 'error' ? 'bg-red-100' :
                        log.level === 'warn' ? 'bg-yellow-100' :
                        'bg-blue-100'
                      }`}>
                        <div className="flex justify-between">
                          <span className="font-bold">[{log.level.toUpperCase()}]</span>
                          <span className="text-gray-500">{new Date(log.timestamp).toLocaleTimeString()}</span>
                        </div>
                        <div className="mt-1">{log.message}</div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* All Logs Tab */}
        <TabsContent value="all" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Todos os Logs</CardTitle>
              <CardDescription>Últimos 200 logs do servidor</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {allLogsQuery.isLoading && <p>Carregando...</p>}
              {allLogsQuery.error && <p className="text-red-600">Erro: {allLogsQuery.error.message}</p>}
              {allLogsQuery.data && (
                <div className="space-y-2 max-h-96 overflow-auto">
                  {(allLogsQuery.data as any).length === 0 ? (
                    <p className="text-gray-500">Nenhum log encontrado</p>
                  ) : (
                    (allLogsQuery.data as any).map((log: any, idx: number) => (
                      <div key={idx} className={`p-2 rounded text-sm font-mono ${
                        log.level === 'error' ? 'bg-red-100' :
                        log.level === 'warn' ? 'bg-yellow-100' :
                        'bg-blue-100'
                      }`}>
                        <div className="flex justify-between">
                          <span className="font-bold">[{log.level.toUpperCase()}]</span>
                          <span className="text-gray-500">{new Date(log.timestamp).toLocaleTimeString()}</span>
                        </div>
                        <div className="mt-1">{log.message}</div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
